import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  DownloadSimple,
  MagnifyingGlass,
  Eye,
  Trash,
  EnvelopeSimple,
  Phone,
  CheckCircle,
  Clock,
  X,
  PaperPlaneTilt,
  Lightning,
} from "@phosphor-icons/react";
import {
  adminFetchQuotes,
  adminFetchQuote,
  adminUpdateQuote,
  adminDeleteQuote,
  adminCsvUrl,
  adminFetchDripsForQuote,
  adminRunDripsNow,
  adminQuoteDripOptOut,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminLayout from "@/pages/AdminLayout";

const STATUS_OPTIONS = [
  { id: "new", label: "New", color: "#22C55E" },
  { id: "contacted", label: "Contacted", color: "#A1A1AA" },
  { id: "won", label: "Won", color: "#22D3EE" },
  { id: "lost", label: "Lost", color: "#F87171" },
];

export default function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchQuotes();
      setQuotes(data);
    } catch (e) {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return quotes.filter((row) => {
      if (filter !== "all" && (row.status || "new") !== filter) return false;
      if (!q) return true;
      return [row.name, row.email, row.phone, row.flake_name, row.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [quotes, query, filter]);

  const stats = useMemo(() => {
    const total = quotes.length;
    const byStatus = STATUS_OPTIONS.reduce((acc, s) => {
      acc[s.id] = 0;
      return acc;
    }, {});
    quotes.forEach((q) => {
      const s = q.status || "new";
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    const last7 = quotes.filter((q) => {
      const t = new Date(q.created_at).getTime();
      return Date.now() - t < 7 * 24 * 60 * 60 * 1000;
    }).length;
    return { total, byStatus, last7 };
  }, [quotes]);

  const downloadCsv = async () => {
    try {
      const { url, headers } = adminCsvUrl();
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error("CSV download failed");
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `new-era-leads-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const openDetail = async (id) => {
    try {
      const full = await adminFetchQuote(id);
      setDetail(full);
    } catch {
      toast.error("Could not load lead.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateQuote(id, { status });
      setQuotes((qs) =>
        qs.map((q) => (q.id === id ? { ...q, status } : q)),
      );
      setDetail((d) => (d && d.id === id ? { ...d, status } : d));
      toast.success("Status updated");
    } catch {
      toast.error("Status update failed");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await adminDeleteQuote(id);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setDetail(null);
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AdminLayout active="leads">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">
            Leads
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Every quote submitted from the visualizer.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={async () => {
              try {
                const { sent } = await adminRunDripsNow();
                toast.success(
                  sent
                    ? `Sent ${sent} drip message${sent === 1 ? "" : "s"}`
                    : "No drips due right now",
                );
              } catch {
                toast.error("Drip run failed");
              }
            }}
            variant="ghost"
            className="text-zinc-200 hover:text-white hover:bg-white/5 border border-white/10"
            data-testid="run-drips-now"
          >
            <Lightning size={16} className="mr-2" /> Run drips now
          </Button>
          <Button
            onClick={downloadCsv}
            className="text-white font-semibold"
            style={{ background: "var(--epx-primary)" }}
            data-testid="export-csv"
          >
            <DownloadSimple size={16} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total leads" value={stats.total} highlight />
        <StatCard label="Last 7 days" value={stats.last7} />
        {STATUS_OPTIONS.map((s) => (
          <StatCard
            key={s.id}
            label={s.label}
            value={stats.byStatus[s.id] || 0}
            color={s.color}
          />
        ))}
      </div>

      <div
        className="rounded-2xl border overflow-hidden"
        style={{
          background: "var(--epx-surface)",
          borderColor: "var(--epx-border)",
        }}
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between px-4 sm:px-5 py-4 border-b" style={{ borderColor: "var(--epx-border)" }}>
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, message…"
              className="pl-9 bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
              data-testid="leads-search"
            />
          </div>
          <div className="flex gap-1 overflow-x-auto">
            <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
              All
            </FilterPill>
            {STATUS_OPTIONS.map((s) => (
              <FilterPill
                key={s.id}
                active={filter === s.id}
                onClick={() => setFilter(s.id)}
                color={s.color}
                testId={`filter-${s.id}`}
              >
                {s.label}
              </FilterPill>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-zinc-500" data-testid="leads-loading">
            Loading leads…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-zinc-500" data-testid="leads-empty">
            {quotes.length === 0
              ? "No leads yet — share your visualizer to start collecting them."
              : "No leads match this filter."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-zinc-400 border-b" style={{ borderColor: "var(--epx-border)" }}>
                  <th className="px-4 sm:px-5 py-3 font-medium">Name</th>
                  <th className="px-3 py-3 font-medium">Contact</th>
                  <th className="px-3 py-3 font-medium">Flake</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">When</th>
                  <th className="px-3 py-3 font-medium text-right pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <tr
                    key={q.id}
                    className="border-b hover:bg-white/[0.02] transition-colors"
                    style={{ borderColor: "var(--epx-border)" }}
                    data-testid={`lead-row-${q.id}`}
                  >
                    <td className="px-4 sm:px-5 py-3 font-medium">{q.name}</td>
                    <td className="px-3 py-3 text-zinc-300">
                      <div className="flex flex-col gap-0.5">
                        <a className="hover:text-white" href={`mailto:${q.email}`}>
                          <EnvelopeSimple size={12} className="inline mr-1" />
                          {q.email}
                        </a>
                        <a className="hover:text-white" href={`tel:${q.phone}`}>
                          <Phone size={12} className="inline mr-1" />
                          {q.phone}
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-zinc-300">{q.flake_name || "—"}</td>
                    <td className="px-3 py-3">
                      <StatusSelect
                        value={q.status || "new"}
                        onChange={(s) => updateStatus(q.id, s)}
                      />
                    </td>
                    <td className="px-3 py-3 text-zinc-400 whitespace-nowrap">
                      {fmtDate(q.created_at)}
                    </td>
                    <td className="px-3 py-3 text-right pr-5">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(q.id)}
                          className="text-zinc-300 hover:text-white hover:bg-white/5"
                          data-testid={`view-lead-${q.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteLead(q.id)}
                          className="text-zinc-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-lead-${q.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeadDetailDialog
        lead={detail}
        onClose={() => setDetail(null)}
        onStatus={(s) => detail && updateStatus(detail.id, s)}
        onDelete={() => detail && deleteLead(detail.id)}
      />
    </AdminLayout>
  );
}

function StatCard({ label, value, color, highlight }) {
  return (
    <div
      className="rounded-xl border p-4"
      style={{
        background: highlight
          ? "linear-gradient(160deg, rgba(var(--epx-primary-rgb),0.14), rgba(var(--epx-primary-rgb),0) 60%), var(--epx-surface)"
          : "var(--epx-surface)",
        borderColor: "var(--epx-border)",
      }}
    >
      <p className="text-xs text-zinc-400">{label}</p>
      <p
        className="font-heading text-2xl sm:text-3xl font-bold mt-1"
        style={color ? { color } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function FilterPill({ active, onClick, color, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-medium border whitespace-nowrap transition-colors ${
        active
          ? "text-white border-transparent"
          : "text-zinc-400 border-white/10 hover:text-white hover:bg-white/5"
      }`}
      style={
        active
          ? { background: color || "var(--epx-primary)" }
          : undefined
      }
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function StatusSelect({ value, onChange }) {
  const cur = STATUS_OPTIONS.find((s) => s.id === value) || STATUS_OPTIONS[0];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-white/30"
      style={{ color: cur.color }}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s.id} value={s.id} style={{ color: "#0a0a0a" }}>
          {s.label}
        </option>
      ))}
    </select>
  );
}

function LeadDetailDialog({ lead, onClose, onStatus, onDelete }) {
  const [drips, setDrips] = useState([]);

  useEffect(() => {
    if (!lead?.id) {
      setDrips([]);
      return;
    }
    adminFetchDripsForQuote(lead.id)
      .then(setDrips)
      .catch(() => setDrips([]));
  }, [lead?.id]);

  if (!lead) return null;

  const stopDrips = async () => {
    try {
      await adminQuoteDripOptOut(lead.id);
      toast.success("Drip cancelled");
      setDrips((ds) =>
        ds.map((d) =>
          d.status === "queued"
            ? { ...d, status: "cancelled", result: "admin-opt-out" }
            : d,
        ),
      );
    } catch {
      toast.error("Could not cancel drip");
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto border"
        style={{
          background: "var(--epx-surface)",
          borderColor: "var(--epx-border)",
          color: "white",
        }}
        data-testid="lead-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{lead.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <Field label="Email" value={<a href={`mailto:${lead.email}`} className="text-green-400 hover:underline">{lead.email}</a>} />
          <Field label="Phone" value={<a href={`tel:${lead.phone}`} className="text-green-400 hover:underline">{lead.phone}</a>} />
          <Field label="Flake" value={lead.flake_name || lead.flake_id || "—"} />
          <Field label="Submitted" value={fmtDate(lead.created_at, true)} />
          <Field label="SMS opt-in" value={lead.sms_opt_in ? <CheckCircle size={16} className="inline text-green-400" /> : <X size={16} className="inline text-zinc-500" />} />
          <Field label="Status" value={
            <select
              value={lead.status || "new"}
              onChange={(e) => onStatus(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white"
              data-testid="detail-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id} style={{ color: "#0a0a0a" }}>{s.label}</option>
              ))}
            </select>
          } />
        </div>
        {lead.message && (
          <div className="mt-4">
            <p className="text-xs text-zinc-400 uppercase tracking-wider">Message</p>
            <p className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap leading-relaxed bg-black/30 border border-white/5 rounded-lg p-3">
              {lead.message}
            </p>
          </div>
        )}
        <div className="mt-4 text-xs text-zinc-500 flex flex-wrap gap-3">
          <span><Clock size={12} className="inline mr-1" />Admin email: {lead.email_status || "—"}</span>
          <span><Clock size={12} className="inline mr-1" />Customer SMS: {lead.sms_status || "—"}</span>
        </div>

        <DripTimeline drips={drips} onStop={stopDrips} />

        {lead.preview_image_base64 && (
          <div className="mt-5">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Preview submitted by customer</p>
            <img
              src={lead.preview_image_base64}
              alt="Customer preview"
              className="w-full rounded-lg border border-white/10"
              data-testid="lead-preview-image"
            />
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onDelete}
            className="text-red-300 hover:bg-red-500/10"
            data-testid="detail-delete"
          >
            <Trash size={14} className="mr-1.5" /> Delete
          </Button>
          <Button
            onClick={onClose}
            className="text-white"
            style={{ background: "var(--epx-primary)" }}
            data-testid="detail-close"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-black/30 border border-white/5 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </p>
      <div className="mt-1 text-sm text-zinc-100">{value}</div>
    </div>
  );
}

function DripTimeline({ drips, onStop }) {
  if (!drips || drips.length === 0) return null;
  const hasQueued = drips.some((d) => d.status === "queued");
  return (
    <div
      className="mt-5 rounded-lg border p-4"
      style={{
        background: "rgba(34,197,94,0.04)",
        borderColor: "rgba(34,197,94,0.18)",
      }}
      data-testid="drip-timeline"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <PaperPlaneTilt
            size={16}
            weight="fill"
            style={{ color: "var(--epx-primary)" }}
          />
          <p className="text-sm font-semibold">Drip campaign</p>
        </div>
        {hasQueued && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onStop}
            className="text-zinc-300 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
            data-testid="drip-stop"
          >
            Stop remaining
          </Button>
        )}
      </div>
      <ol className="space-y-2">
        {drips.map((d) => (
          <li
            key={d.id}
            className="flex items-start gap-3 text-xs"
            data-testid={`drip-step-${d.step}`}
          >
            <DripStatusDot status={d.status} />
            <div className="flex-1 min-w-0">
              <p className="text-zinc-200 font-medium">
                Step {d.step} ·{" "}
                {d.step === 1
                  ? "Your floor preview is ready"
                  : "Still thinking it over?"}
              </p>
              <p className="text-zinc-500 mt-0.5">
                {d.status === "sent"
                  ? `Sent ${fmtDate(d.sent_at, true)}`
                  : d.status === "queued"
                    ? `Scheduled ${fmtDate(d.send_at, true)} · ${d.result || "waiting"}`
                    : d.status === "cancelled"
                      ? `Cancelled · ${d.result || ""}`
                      : `Failed · ${d.result || ""}`}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DripStatusDot({ status }) {
  const map = {
    sent: { bg: "#22c55e", label: "✓" },
    queued: { bg: "#facc15", label: "·" },
    cancelled: { bg: "#71717a", label: "—" },
    failed: { bg: "#f87171", label: "!" },
  };
  const s = map[status] || map.queued;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-black"
      style={{ background: s.bg }}
    >
      {s.label}
    </span>
  );
}

function fmtDate(iso, full = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (full) return d.toLocaleString();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
